<template>
    <div>
        <div id="intro">
            <VideoBlock
                :scrollProgress="totalScrollProgress"
                :class="
                    stickyContent == 'VideoBlock'
                        ? 'active-intro'
                        : 'inactive-intro'
                "
            ></VideoBlock>
            <MapBlock
                :class="
                    stickyContent == 'MapBlock'
                        ? 'active-intro'
                        : 'inactive-intro'
                "
                :isInteractive="mapInteractive"
            >
            </MapBlock>
        </div>
        <div id="cover">
            <img src="@/assets/gfx/logo_ruimteschip_aarde.svg" loading="lazy" />
            <h1>Ruimteschip Aarde</h1>
            <h4 v-html="appConfig.coverTitle"></h4>
            <div>
                <div class="triggerV">&#xEA09;</div>
                <div>Scroll naar beneden</div>
            </div>
        </div>
        <div id="features">
            <div
                v-for="(chapter, index) in appConfig.chapters"
                :id="chapter.id"
                :data-step-no="index"
                :key="index"
                :class="[
                    'step',
                    chapter.alignment == 'center'
                        ? 'centered'
                        : chapter.alignment + 'y',
                    chapter.hidden ? 'hiddenstep' : '',
                ]"
            >
                <div class="dark" v-if="chapter.video">
                    <div class="video-player">
                        <VideoChapter
                            :videoSrc="chapter.video"
                            :ref="`video${index}`"
                        />
                        <div class="click-to-unmute-overlay">
                            <div
                                class="mute-icon"
                                role="button"
                                @click="toggleMute()"
                            ></div>
                        </div>
                    </div>
                    <p v-html="chapter.alt" class="media-description"></p>
                </div>
                <div class="dark" v-else-if="chapter.image">
                    <ImageChapter :imageSrc="chapter.image" />
                    <p v-html="chapter.alt" class="media-description"></p>
                </div>
                <div class="dark" v-else>
                    <p v-html="chapter.description"></p>
                </div>
            </div>
        </div>
        <TitleBlock
            v-show="mapInteractive"
            :mapTitle="appConfig.mapTitle"
            :themeURL="appConfig.themeURL"
        ></TitleBlock>
        <GeoCoder v-show="mapInteractive"></GeoCoder>
        <LegendBlock
            v-show="showLegend"
            :classBoundaries="appConfig.classBoundaries"
            :legendColours="appConfig.legendColours"
            :legendSubTitle="appConfig.legendSubTitle"
            :legendTitle="appConfig.legendTitle"
        ></LegendBlock>
        <ColophonBlock
            v-show="mapInteractive"
            :moreURL="appConfig.moreURL"
            :uitlegURL="appConfig.uitlegURL"
        ></ColophonBlock>
    </div>
</template>
<script>
import "intersection-observer";
import ColophonBlock from "@/components/ColophonBlock.vue";
import GeoCoder from "@/components/GeoCoder.vue";
import ImageChapter from "@/components/ImageChapter.vue";
import LegendBlock from "@/components/LegendBlock.vue";
import MapBlock from "@/components/MapBlock.vue";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import maplibregl from "maplibre-gl";
import { pianoAnalytics } from "piano-analytics-js";
import scrollama from "scrollama";
import TitleBlock from "@/components/TitleBlock.vue";
import VideoBlock from "@/components/VideoBlock.vue";
import VideoChapter from "@/components/VideoChapter.vue";

let navigation;

export default {
    components: {
        ColophonBlock,
        GeoCoder,
        ImageChapter,
        LegendBlock,
        MapBlock,
        TitleBlock,
        VideoBlock,
        VideoChapter,
    },
    data() {
        return {
            currStepProgress: 0,
            totalScrollProgress: 0,
            showLegend: false,
            map: {},
            mapInteractive: false,
            midpoint: 0,
            popup: {},
            stickyContent: "VideoBlock",
            scroller: scrollama(),
            themeValue: 0,
            videoMuted: true,
            appConfig: {},
        };
    },
    async mounted() {
        await this.getConfiguration();
        this.scrollIntoView();
        this.resizeHandler();
        window.addEventListener("resize", this.resizeHandler);
        this.createMap();
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.resizeHandler);
    },
    methods: {
        async getConfiguration() {
            await fetch(
                process.env.VUE_APP_API_BASE_URL +
                    "/media/" +
                    process.env.VUE_APP_THEME +
                    "/config.json"
            )
                .then((res) => res.json())
                .then((data) => {
                    this.appConfig = data;
                })
                .catch((err) => console.log(err.message));
        },
        scrollIntoView() {
            this.$nextTick(function () {
                if (window.location.hash === "#postcodetool") {
                    const el = this.$el.querySelector("#postcodetool");
                    if (el) {
                        el.scrollIntoView({ behavior: "smooth", block: "end" });
                    }
                }
            });
        },
        resizeHandler() {
            let vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty("--vh", `${vh}px`);
            this.midpoint = Math.floor(vh * 100) + "px";
        },
        toggleMute() {
            if (!this.videoMuted) {
                Object.keys(this.$refs).forEach((vid) => {
                    this.$refs[vid][0].$el.muted = true;
                });
                this.videoMuted = true;
            } else {
                Object.keys(this.$refs).forEach((vid) => {
                    this.$refs[vid][0].$el.muted = false;
                });
                this.videoMuted = false;
            }
        },
        createMap() {
            if (this.isIOS) {
                maplibregl.clearStorage();
            }
            const initialState = {
                lng: 5.325,
                lat: 52.072,
                zoom: 8,
                bearing: 0,
                pitch: 40,
            };
            this.map = new maplibregl.Map({
                bearing: initialState.bearing,
                center: [initialState.lng, initialState.lat],
                container: "map",
                doubleClickZoom: false,
                dragPan: false,
                dragRotate: false,
                locale: {
                    "AttributionControl.ToggleAttribution": "Bronvermelding",
                    "NavigationControl.ZoomIn": "Zoom in",
                    "NavigationControl.ZoomOut": "Zoom uit",
                },
                maxBounds: [
                    [2.25, 50],
                    [7.75, 54],
                ],
                maxTileCacheSize: this.maximumTileCacheSize,
                maxZoom: 16,
                navigation: false,
                pitch: initialState.pitch,
                scrollZoom: false,
                style:
                    process.env.VUE_APP_API_BASE_URL + this.appConfig.mapStyle,
                touchPitch: false,
                touchZoomRotate: false,
                zoom: initialState.zoom,
            });
            navigation = new maplibregl.NavigationControl({
                showCompass: false,
            });

            let geocoder = new MapboxGeocoder({
                accessToken: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN,
                countries: "nl",
                language: "nl",
                enableEventLogging: false,
                placeholder: "Adres, postcode of plaats",
                types: "region, postcode, district, place, locality, neighborhood, address",
                marker: false,
            });

            pianoAnalytics.setConfigurations({
                site: process.env.VUE_APP_PA_SITE,
                collectDomain: process.env.VUE_APP_PA_DOMAIN,
            });

            document
                .getElementById("geocoder")
                .appendChild(geocoder.onAdd(this.map));

            geocoder.on("result", () => {
                pianoAnalytics.sendEvent(
                    "click.navigation", // Event name
                    {
                        click: "geocoderen", // Event properties dit kan naaam van postcode veld of link naam zijn
                        click_chapter1: "programmas",
                        click_chapter2: "ruimteschip-aarde",
                        click_chapter3: "postcode-tool",
                        page: "ruimteschip-aarde-" + process.env.VUE_APP_THEME, // Pagina titels: ruimteschip-aarde-lucht, ruimteschip-aarde-water, ruimteschip-aarde-bodem
                    }
                );
            });

            this.popup = new maplibregl.Popup({
                closeButton: false,
                closeOnClick: false,
                maxWidth: "none",
            });

            this.map.on("mousemove", "3D-BAG", (e) => {
                this.onMapMoveMouse(e);
            });

            this.map.on("mouseleave", "3D-BAG", () => {
                this.onMapLeaveMouse();
            });
            this.scroller
                .setup({
                    step: "#features .step",
                    offset: this.midpoint,
                    progress: true,
                    threshold: 1,
                    debug: false,
                })
                .onStepEnter(this.stepEnterHandler)
                .onStepProgress(this.stepProgressHandler)
                .onStepExit(this.stepExitHandler);
        },
        getLayerPaintType(layer) {
            if (!this.map.getLayer(layer)) {
                return;
            }
            const layerType = this.map.getLayer(layer).type;
            const layerTypes = {
                // eslint-disable-next-line
                "fill": ["fill-opacity"],
                // eslint-disable-next-line
                "line": ["line-opacity"],
                // eslint-disable-next-line
                "circle": ["circle-opacity", "circle-stroke-opacity"],
                // eslint-disable-next-line
                "symbol": ["icon-opacity", "text-opacity"],
                // eslint-disable-next-line
                "raster": ["raster-opacity"],
                // eslint-disable-next-line
                "fill-extrusion": ["fill-extrusion-opacity"],
            };
            return layerTypes[layerType];
        },
        onMapLeaveMouse() {
            this.popup.remove();
            this.themeValue = 0;
        },
        onMapMoveMouse(event) {
            if (this.mapInteractive) {
                const e = event;
                const features = this.map.queryRenderedFeatures(e.point, {
                    layers: ["3D-BAG"],
                });
                const feature = features[0];

                if (!feature) {
                    this.popup.setHTML("");
                    return;
                }

                if (feature.properties[process.env.VUE_APP_THEME] != null) {
                    if (process.env.VUE_APP_THEME == "water") {
                        if (
                            feature.properties[process.env.VUE_APP_THEME] == 0
                        ) {
                            this.themeValue =
                                "0&nbsp;&ndash;&nbsp;5&nbsp;" +
                                this.appConfig.themeUnit;
                        } else if (
                            feature.properties[process.env.VUE_APP_THEME] == 5
                        ) {
                            this.themeValue =
                                "5&nbsp;&ndash;&nbsp;10&nbsp;" +
                                this.appConfig.themeUnit;
                        } else if (
                            feature.properties[process.env.VUE_APP_THEME] == 10
                        ) {
                            this.themeValue =
                                "10&nbsp;&ndash;&nbsp;15&nbsp;" +
                                this.appConfig.themeUnit;
                        } else if (
                            feature.properties[process.env.VUE_APP_THEME] == 15
                        ) {
                            this.themeValue =
                                "15&nbsp;&ndash;&nbsp;20&nbsp;" +
                                this.appConfig.themeUnit;
                        } else if (
                            feature.properties[process.env.VUE_APP_THEME] == 20
                        ) {
                            this.themeValue =
                                "20&nbsp;&ndash;&nbsp;30&nbsp;" +
                                this.appConfig.themeUnit;
                        } else if (
                            feature.properties[process.env.VUE_APP_THEME] == 30
                        ) {
                            this.themeValue =
                                "&gt;&nbsp;30&nbsp;" + this.appConfig.themeUnit;
                        }
                    } else if (
                        process.env.VUE_APP_THEME == "licht" &&
                        feature.properties[process.env.VUE_APP_THEME] == 2000
                    ) {
                        this.themeValue =
                            "&gt;&nbsp;2000&nbsp;" + this.appConfig.themeUnit;
                    } else {
                        this.themeValue =
                            Intl.NumberFormat("nl-NL").format(
                                feature.properties[process.env.VUE_APP_THEME]
                            ) +
                            "&nbsp;" +
                            this.appConfig.themeUnit;
                    }
                } else {
                    this.themeValue = "Onbekend";
                }

                let address = "";
                if (feature.properties.straat) {
                    address =
                        '<hr /><span class="address">' +
                        feature.properties.straat +
                        "&nbsp;" +
                        feature.properties.huisnummer +
                        feature.properties.toevoeging +
                        "<br />" +
                        (feature.properties.postcode
                            ? feature.properties.postcode + "&nbsp;"
                            : "") +
                        feature.properties.woonplaats +
                        "</span>";
                }

                this.popup
                    .setLngLat(e.lngLat)
                    .setHTML(this.themeValue + address)
                    .addTo(this.map);

                this.popup._update();
            }
        },
        setLayerOpacity(layer) {
            const paintProps = this.getLayerPaintType(layer.layer);
            if (paintProps) {
                paintProps.forEach((prop) => {
                    this.map.setPaintProperty(layer.layer, prop, layer.opacity);
                });
            }
        },
        stepEnterHandler({ index, direction }) {
            const chapter = this.appConfig.chapters[index];
            if (chapter.bgContent && chapter.bgContent != this.stickyContent) {
                this.stickyContent =
                    chapter.bgContent.charAt(0).toUpperCase() +
                    chapter.bgContent.slice(1) +
                    "Block";
            }
            if (chapter.bgContent == "map") {
                if (chapter.location) {
                    this.map[chapter.mapAnimation || "flyTo"](chapter.location);
                }

                if (chapter.onChapterEnter.length > 0) {
                    chapter.onChapterEnter.forEach((layer) => {
                        this.setLayerOpacity(layer);
                    });
                }

                if (chapter.mapInteractive) {
                    this.mapInteractive = chapter.mapInteractive;
                    this.map.addControl(navigation, "top-right");
                    this.map.dragPan.enable();
                    this.map.doubleClickZoom.enable();
                    this.map.touchZoomRotate.enable();
                    this.map.touchZoomRotate.disableRotation();
                    this.map.getCanvas().style.cursor = "grab";
                    this.$el
                        .querySelector(".maplibregl-ctrl-bottom-right")
                        .classList.add("attribution-visible");
                } else {
                    this.mapInteractive = false;
                    if (
                        index == this.appConfig.chapters.length - 2 &&
                        direction == "up"
                    ) {
                        this.map.removeControl(navigation);
                        this.popup.remove();
                    }
                    this.map.dragPan.disable();
                    this.map.doubleClickZoom.disable();
                    this.map.touchZoomRotate.disable();
                    this.map.getCanvas().style.cursor = "default";
                    this.$el
                        .querySelector(".maplibregl-ctrl-bottom-right")
                        .classList.remove("attribution-visible");
                }
            }

            if (typeof chapter.legend === "undefined" || !chapter.legend) {
                this.showLegend = false;
            } else {
                this.showLegend = chapter.legend;
            }

            if (chapter.video) {
                if (this.$refs["video" + index][0]) {
                    this.$refs["video" + index][0].$el.play();
                }
            }
        },
        stepExitHandler({ index }) {
            const chapter = this.appConfig.chapters[index];
            if (chapter.bgContent == "map") {
                if (chapter.onChapterExit.length > 0) {
                    chapter.onChapterExit.forEach((layer) => {
                        this.setLayerOpacity(layer);
                    });
                }
            }
            if (chapter.video) {
                if (this.$refs["video" + index][0]) {
                    this.$refs["video" + index][0].$el.pause();
                }
            }
        },
        stepProgressHandler({ index, progress }) {
            const totalprogress =
                ((parseInt(index) + progress) * 100) / this.videoChapters;
            if (totalprogress < 100) {
                this.totalScrollProgress = totalprogress;
            } else {
                this.totalScrollProgress = 100;
            }
        },
    },
    computed: {
        videoChapters: function () {
            return this.appConfig.chapters.filter(
                (c) => c.bgContent === "video"
            ).length;
        },
        isIOS: function () {
            return /iPhone|iPad|iPod/i.test(navigator.userAgent);
        },
        maximumTileCacheSize: function () {
            if (this.isIOS) {
                return 0;
            } else {
                return null;
            }
        },
    },
};
</script>
<style>
.header__bar {
    display: none !important;
}

#intro {
    width: 100%;
    height: 100vh;
    height: calc(var(--vh, 1vh) * 100);
    top: 0;
    left: 0;
    right: 0;
    transform: translate3d(0, 0, 0);
    position: fixed;
    background-color: #000;
    will-change: opacity;
}

.active-intro {
    width: 100%;
    height: 100vh;
    height: calc(var(--vh, 1vh) * 100);
    display: block;
    animation-duration: 1s;
    animation-name: animate-fade;
    animation-fill-mode: backwards;
}

@keyframes animate-fade {
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
}

.inactive-intro {
    position: relative;
    display: none;
}

#cover {
    width: 100%;
    left: 0;
    right: 0;
    top: 40vh;
    top: calc(var(--vh, 1vh) * 40);
    background-color: rgba(255, 255, 255, 0.95);
    position: relative;
    z-index: 5;
    margin: 10px auto 10px;
    padding-top: 50px;
    padding-bottom: 36px;
    text-align: center;
    align-items: center;
    justify-content: center;
}

#cover img {
    position: absolute;
    z-index: 10;
    top: 0%;
    left: 50%;
    width: clamp(5rem, 15vw, 10rem);
    aspect-ratio: 1/1;
    transform: translate(-50%, -50%);
}

#cover h1 {
    margin-top: 16px;
    line-height: 1 !important;
}

.triggerV {
    color: #1298e0;
    font-family: icon;
    font-size: 2em;
    margin-bottom: 14px;
    animation: bounce 3s infinite;
}

@keyframes bounce {
    0%,
    20%,
    50%,
    80%,
    100% {
        transform: translateY(0);
    }
    40% {
        transform: translateY(30px);
    }
    60% {
        transform: translateY(15px);
    }
}

#cover h4 a:hover {
    color: #1298e0;
}

#features {
    padding-top: 70vh;
    padding-top: calc(var(--vh, 1vh) * 70);
}

.step {
    padding-bottom: 90vh;
    padding-bottom: calc(var(--vh, 1vh) * 90);
    width: 50%;
    margin: 10px auto 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 10;
    font-size: 18px;
    color: #f2e8d0;
}

.hiddenstep {
    visibility: hidden;
}

.centered {
    width: 50vw;
    margin: 0 auto;
}

.lefty {
    width: 33vw;
    margin-left: 5vw;
}

.righty {
    width: 33vw;
    margin-right: 5vw;
}

.fully {
    width: 100%;
    margin: auto;
}

.dark {
    background-color: #0a140f;
}

.dark .video-player {
    position: relative;
    overflow: hidden;
}

.dark video {
    height: auto;
    width: 100%;
}

.dark .click-to-unmute-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
}

.dark .mute-icon {
    position: absolute;
    z-index: 2;
    width: 40px;
    height: 40px;
    line-height: 1;
    text-align: center;
    color: #f2e8d0;
    background-color: rgba(18, 152, 224, 1);
    border-radius: 20px;
    transition: color, background-color 0.2s ease;
    top: 10px;
    right: 10px;
    cursor: pointer;
    opacity: 0.7;
}

.dark .mute-icon:hover,
.dark .mute-icon:focus {
    opacity: 1;
}

.dark .mute-icon::after {
    display: inline-block;
    font-style: normal;
    font-variant: normal;
    text-rendering: auto;
    -webkit-font-smoothing: antialiased;
    font-family: icon;
    font-weight: 900;
    font-size: 20px;
    content: "";
    padding-top: 10px;
    padding-left: 5px;
}

.dark p {
    font-weight: normal;
    width: 15vw;
    margin-bottom: 0;
    padding: 36px;
}

.dark p em {
    font-style: normal;
    font-weight: bold;
    color: #fff;
}

.dark p.media-description {
    width: 100%;
}

footer.footer {
    display: none !important;
}

/* POPUP */
.maplibregl-popup {
    opacity: 0.9;
}
.maplibregl-popup-content {
    background-color: #06305e;
    color: #fff;
    /* font-family: "PPFormulaCondensed"; */
    font-size: 24px;
    font-weight: bold;
    text-align: center;
    padding: 16px 24px 10px 24px;
}

.maplibregl-popup-content hr {
    margin-top: 2px;
    margin-bottom: 2px;
}

.maplibregl-popup-content .address {
    font-family: "Inter", sans-serif;
    font-size: 16px;
    line-height: 0.8;
}

.maplibregl-popup-anchor-bottom-left .maplibregl-popup-tip,
.maplibregl-popup-anchor-bottom-right .maplibregl-popup-tip,
.maplibregl-popup-anchor-bottom .maplibregl-popup-tip {
    border-top-color: #06305e;
}
.maplibregl-popup-anchor-top-left .maplibregl-popup-tip,
.maplibregl-popup-anchor-top-right .maplibregl-popup-tip,
.maplibregl-popup-anchor-top .maplibregl-popup-tip {
    border-bottom-color: #06305e;
}
.maplibregl-popup-anchor-right .maplibregl-popup-tip {
    border-left-color: #06305e;
}
.maplibregl-popup-anchor-left .maplibregl-popup-tip {
    border-right-color: #06305e;
}
.popup-content {
    padding: 0;
}
.maplibregl-popup-content em {
    font-size: 16px;
}

@media (max-width: 1440px) {
    .lefty {
        width: 40vw;
        margin-left: 3vw;
    }

    .righty {
        width: 40vw;
        margin-right: 3vw;
    }

    .dark p {
        width: 20vw;
    }
}

@media (max-width: 992px) {
    #cover h4 {
        margin: 0 10vw;
    }

    #features {
        width: 70vw;
        margin: 0 auto;
    }

    .centered,
    .lefty,
    .righty,
    .fully {
        width: 70vw;
        margin: 0 auto;
    }
    .dark video {
        height: auto;
        width: 100%;
    }
    .dark p {
        width: 100%;
    }
}

@media (max-width: 750px) {
    #cover h4 {
        margin: 0 5vw;
    }
    #features {
        width: 90vw;
        margin: 0 auto;
    }

    .lefty {
        width: 90%;
    }

    .centered,
    .lefty,
    .righty,
    .fully {
        width: 90vw;
        margin: 0 auto;
    }

    .dark p {
        width: 100%;
        padding: 24px;
    }
}
</style>
